import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

/**
 * ⚠ **A CARD FILLS THE HEIGHT ITS ROW GIVES IT** (BRIEF, app-wide rule, Jesse
 * 2026-08-12: *"In general, can we have each row's card fit the height given to
 * it."*). It is implemented HERE, in the primitive and its row wrapper, rather
 * than screen by screen — which is how the app ended up with four month pickers
 * and three runway calculations.
 *
 * **The four parts, and every screen gets them for free:**
 *
 * 1. **`CardRow`** lays a row out as a grid, which is `items-stretch` — so every
 *    card in the row is handed the height of the tallest one.
 * 2. **`Card` is a flex column** (`flex flex-col`), so that height can travel
 *    down into its contents instead of stopping at the border.
 * 3. **`CardBody` is `flex-1 min-h-0`**, so the body — chart, list, table — is
 *    the part that absorbs it. `ChartFrame` is the same idea for a plot area:
 *    the chart expands rather than sitting at a hard-coded `h-72` above a band
 *    of dead white space. `CardFooter` and `StatCard`'s `footer` are pinned to
 *    the bottom edge (`mt-auto`) so a meter under a figure lands ON the edge
 *    rather than floating above it.
 * 4. ⚠ **`fill` BOUNDS IT.** Filling is not chasing. `fill="sm|md|lg"` caps how
 *    tall a card may grow, so one unusually tall neighbour (the dashboard's
 *    "Needs attention" with twenty late invoices) cannot stretch a chart to an
 *    absurd height: past the cap the extra height belongs to the tall
 *    neighbour and the shorter card simply ends. `fill="natural"` is the other
 *    end of the same dial — a card whose body genuinely cannot use extra height
 *    (two rows of a list beside a full chart) opts out of stretching entirely
 *    rather than printing a dead band.
 *
 * **Choosing a `fill`** — the whole decision in four lines:
 *  - body is a chart or a scrollable/distributing list → leave it (default).
 *  - chart sharing a row with a deliberately variable list → `sm`/`md`/`lg`.
 *  - short, fixed body beside a much taller sibling → `natural`.
 *  - a strip of KPI tiles → leave it; uniform tiles are the point.
 *
 * Caps are `md:`-prefixed on purpose: below `md` every row in this app is one
 * column, so the rule is moot and a max-height could only ever clip a phone.
 */

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

/**
 * How tall a card may grow in its row — see the header block. Exported as a
 * plain table because the shells that must render a real `<a>`/`<button>`
 * (`StatCard`'s navigational tiles) cannot be a `Card` and would otherwise
 * need a second spelling of these caps, which is how a clickable KPI tile and
 * a reporting one in the same strip start behaving differently.
 */
export type CardFill = 'grow' | 'sm' | 'md' | 'lg' | 'natural';
export const CARD_FILL_CLASS: Record<CardFill, string> = {
  grow: '',
  sm: 'md:max-h-[30rem]',
  md: 'md:max-h-[35rem]',
  lg: 'md:max-h-[40rem]',
  natural: 'self-start',
};

const cardVariants = cva('bg-white rounded-xl border flex flex-col', {
  variants: {
    variant: {
      default: 'shadow-card border-grey-200/50',
      hover: 'shadow-card border-grey-200/50 transition-all duration-200 hover:shadow-elevated hover:border-grey-300',
      flat: 'border-grey-200',
    },
    padding: {
      none: '',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
    },
    /** See the header block: how tall this card may grow in its row. */
    fill: CARD_FILL_CLASS,
  },
  defaultVariants: {
    variant: 'default',
    padding: 'none',
    fill: 'grow',
  },
});

export interface CardProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, padding, fill, ...props }, ref) => (
    <div ref={ref} className={cn(cardVariants({ variant, padding, fill }), className)} {...props} />
  ),
);
Card.displayName = 'Card';

// ---------------------------------------------------------------------------
// CardRow — the row half of the rule
// ---------------------------------------------------------------------------

/**
 * The row shapes this app actually ships, named rather than re-typed. They
 * differ in WHERE they split, and that difference is deliberate: two cards
 * carrying charts need real width before they are worth splitting, while two
 * cards carrying figures are fine at tablet size.
 */
const cardRowVariants = cva('grid items-stretch gap-4', {
  variants: {
    cols: {
      /** Two equal columns from `md` — half/half of text, figures or a meter. */
      2: 'md:grid-cols-2',
      /** Two, but only from `xl`: a pair of CHARTS squeezed into 370px at `md`
       *  loses its axis labels before it loses its white space. */
      '2-xl': 'xl:grid-cols-2',
      /** Three from `xl` — put `xl:col-span-2` on the wide card. */
      3: 'xl:grid-cols-3',
      /** Three from `lg`, where the wide card is a chart and the narrow one is
       *  a short list that can live at ~330px. */
      '3-lg': 'lg:grid-cols-3',
      /** A KPI strip of four: 2-up on a phone, 4-up on a wide screen. */
      4: 'grid-cols-2 xl:grid-cols-4',
      /** A KPI strip of five — the money dashboard's. */
      5: 'grid-cols-2 xl:grid-cols-5',
    },
  },
  defaultVariants: { cols: 2 },
});

export interface CardRowProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardRowVariants> {}

/**
 * A row of cards.
 *
 * `items-stretch` is the grid default, but it is written here explicitly
 * because it is the load-bearing half of the fill rule and the thing a future
 * reader is most likely to "fix" with `items-start` after seeing one card with
 * a dead band. The right lever for that card is `<Card fill="natural">` — one
 * card opting out — not the whole row giving up on matching heights.
 */
export function CardRow({ className, cols, ...props }: CardRowProps) {
  return <div className={cn(cardRowVariants({ cols }), className)} {...props} />;
}

// ---------------------------------------------------------------------------
// Slots
// ---------------------------------------------------------------------------

export function CardHeader({
  title,
  subtitle,
  actions,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-start justify-between gap-4 px-6 pt-5 pb-4', className)}>
      <div>
        <h3 className="text-heading-4 text-secondary">{title}</h3>
        {subtitle && <p className="text-body-sm text-grey-500 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Hand the row's height down to the body's own content.
   *
   * `CardBody` always *takes* the height (`flex-1`); `fill` makes it *pass it
   * on* — the body becomes a flex column and its single child is stretched, so
   * a meter can pin itself to the card's bottom edge or a list can spread its
   * rows rather than leaving a gap under the last one. The primitive supplies
   * the height; the content decides how to spend it (`justify-between`,
   * `flex-1` on a `<ul>`, …). Injecting that decision here would be wrong —
   * spreading a legend away from the list it labels is worse than the gap.
   */
  fill?: boolean;
}

export function CardBody({ className, fill, ...props }: CardBodyProps) {
  // `flex-1 min-h-0` is the other half of the fill rule above: the body is the
  // part that grows. `min-w-0` keeps a wide child (a table in `overflow-x-auto`)
  // able to shrink now that the card is a flex container.
  return (
    <div
      className={cn(
        'px-6 pb-6 flex-1 min-h-0 min-w-0',
        fill && 'flex flex-col [&>*]:min-h-0 [&>:only-child]:flex-1',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Pinned to the card's bottom edge (`mt-auto`), whatever height the row handed
 * the card. This is the answer to *"never a visible gap between the last item
 * and the card's bottom edge"* for content that belongs at the bottom — a
 * meter, a total, a footing link.
 */
export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mt-auto px-6 pb-6 pt-4', className)} {...props} />;
}

// ---------------------------------------------------------------------------
// ChartFrame — the one plot-area wrapper
// ---------------------------------------------------------------------------

const chartFrameVariants = cva('min-h-0 flex-1 w-full', {
  variants: {
    /**
     * The floor: how short the plot may get before it stops being readable.
     * There is no ceiling here — the ceiling belongs to the CARD (`fill`), so
     * one bound governs the whole card rather than the plot and its chrome
     * disagreeing about where the card ends.
     */
    min: {
      xs: 'min-h-[7rem]',
      sm: 'min-h-[13rem]',
      md: 'min-h-[15rem]',
      lg: 'min-h-[19rem]',
    },
  },
  defaultVariants: { min: 'md' },
});

export interface ChartFrameProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof chartFrameVariants> {}

/**
 * The plot area of a card-borne chart: it **expands into the height the row
 * gave the card** instead of standing at a hard-coded `h-72` with white space
 * under it — Jesse's *"can we have the graph fill in the height that is given
 * to it?"*.
 *
 * Wrap a Recharts `<ResponsiveContainer width="100%" height="100%">` in it.
 * `min` sets the readable floor; the ceiling is the card's `fill`.
 */
export function ChartFrame({ className, min, ...props }: ChartFrameProps) {
  return <div className={cn(chartFrameVariants({ min }), className)} {...props} />;
}
